import * as THREE from 'three';
import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import NeonObstacle from './NeonObstacle.jsx';

// ==========================================
// 1. GRAFS COMPLETS I PLATÒNICS (Regulars)
// ==========================================

// K4 (Graf Complet de 4 nodes). Cada node connecta amb tots els altres.
export function NeonTetra({ color = '#00ffff', scale = 1, ...props }) {
    const geometry = useMemo(() => new THREE.TetrahedronGeometry(1), []);
    return <NeonObstacle geometry={geometry} color={color} scale={scale} {...props} />;
}

// Q3 (Graf hipercub 3D). Producte cartesià de 3 arestes (K2 ◻ K2 ◻ K2).
export function NeonCube({ color = '#ff0055', scale = 1, ...props }) {
    const geometry = useMemo(() => new THREE.BoxGeometry(1.5, 1.5, 1.5), []);
    return <NeonObstacle geometry={geometry} color={color} scale={scale} {...props} />;
}

// Graf Octaèdric (Regular de grau 4). 
export function NeonOcta({ color = '#93ffca', scale = 1, ...props }) {
    const geometry = useMemo(() => new THREE.OctahedronGeometry(1.2), []);
    return <NeonObstacle geometry={geometry} color={color} scale={scale} {...props} />;
}

// Graf Dodecaèdric (Regular de grau 3, 20 vèrtexs). 
export function NeonDodeca({ color = '#ffbb00', scale = 1, ...props }) {
    const geometry = useMemo(() => new THREE.DodecahedronGeometry(1.5), []);
    return <NeonObstacle geometry={geometry} color={color} scale={scale} {...props} />;
}

// Graf Icosaèdric (Regular de grau 5, 12 vèrtexs).
export function NeonIcosa({ color = '#ff00ff', scale = 1, ...props }) {
    const geometry = useMemo(() => new THREE.IcosahedronGeometry(1.5), []);
    return <NeonObstacle geometry={geometry} color={color} scale={scale} {...props} />;
}

// ==========================================
// 2. GRAFS PRODUCTE (Cartesian Products)
// ==========================================

// Torus Grid (Producte Cartesià de dos cicles: Cm ◻ Cn).
// Crea una graella tancada sobre si mateixa com un dònut (Torus).
export function NeonTorusGrid({ color = '#00ffaa', scale = 1, tube = 0.4, radial = 6, tubular = 16, ...props }) {
    const geometry = useMemo(() => new THREE.TorusGeometry(1.5, tube, radial, tubular), [tube, radial, tubular]);
    return <NeonObstacle geometry={geometry} color={color} scale={scale} {...props} />;
}

// Cilindre Mesh (Producte Cartesià d'un cicle i un camí: Cn ◻ Pm).
// 'openEnded' fa que no hi hagi tapes, sent purament l'esquelet producte.
export function NeonCylinderGraph({ color = '#0055ff', scale = 1, radius = 1, height = 3, radialSegments = 8, heightSegments = 4, ...props }) {
    const geometry = useMemo(() => new THREE.CylinderGeometry(radius, radius, height, radialSegments, heightSegments, true), [radius, height, radialSegments, heightSegments]);
    return <NeonObstacle geometry={geometry} color={color} scale={scale} {...props} />;
}

// Prism Graph (Graf Prisma). És un cas especial de Cn ◻ K2.
export function NeonPrism({ color = '#ff3300', scale = 1, sides = 5, height = 2, ...props }) {
    const geometry = useMemo(() => new THREE.CylinderGeometry(1, 1, height, sides, 1, false), [sides, height]);
    return <NeonObstacle geometry={geometry} color={color} scale={scale} {...props} />;
}

// ==========================================
// 3. GRAFS COMPLEXES ESPACIALS
// ==========================================

// Torus Knot (Matemàtica de Nusos). Geometria extremadament complexa.
export function NeonTorusKnot({ color = '#ffaa00', scale = 1, ...props }) {
    // P i Q determinen les voltes que fa el nus abans de tancar-se.
    const geometry = useMemo(() => new THREE.TorusKnotGeometry(1.2, 0.3, 64, 8, 2, 3), []);
    return <NeonObstacle geometry={geometry} color={color} scale={scale} {...props} />;
}

// ==========================================
// 4. GRAFS ANIMATS NIDATS (El "Teorema" o Tesseract)
// ==========================================

// Aquest component instància 2 obstacles en la mateixa posició de l'espai i n'aplica
// moviments cinemàtics al seu RigidBody perquè reaccionin i afectin a l'espai global mentre giren en sentits oposats.
export function NeonTesseract({ color = '#ff3333', innerColor = '#ff5328', scale = 1, position = [0, 0, 0], spawnAnimation = false, ...props }) {
    // Cubs molt més petits i posicionats ja isomètricament perquè "mirin de punta" cap a l'usuari (Z)
    const outerGeom = useMemo(() => {
        const geom = new THREE.BoxGeometry(1.2, 1.2, 1.2);
        // Rotació isomètrica perfecta: fa que un vèrtex (punta) quedi exactament cap a nosaltres
        geom.rotateY(Math.PI / 4);
        geom.rotateX(Math.atan(1 / Math.SQRT2));
        return geom;
    }, []);

    const innerGeom = useMemo(() => {
        const geom = new THREE.BoxGeometry(0.5, 0.5, 0.5);
        geom.rotateY(Math.PI / 4);
        geom.rotateX(Math.atan(1 / Math.SQRT2));
        return geom;
    }, []);

    const outerRef = useRef();
    const innerRef = useRef();

    // Per al moviment a la dreta continu basat en spawn
    const xOffset = useRef(0);

    useFrame((state, delta) => {
        // En un Type="fixed" no ho podriem fer, però gràcies al KinematicPosition, 
        // podem mutar el 'RigidBody' (referenciat a través del forwardRef) frame a frame!

        // Moviment de desplaçament EN CONTRA del jugador (d'un pla distant a propar-se molt ràpidament!)
        if (spawnAnimation) {
            xOffset.current -= delta * 8; // Abans anava a 12, que sumat a la nau (+5.5) era letal. Ara és més assequible.
        }

        // 1. Cub Gran (Gir Antihorari exclusivament a l'eix Z)
        if (outerRef.current) {
            const rot = outerRef.current.rotation(); // Retorna Rapier Quaternion
            const euler = new THREE.Euler().setFromQuaternion(new THREE.Quaternion(rot.x, rot.y, rot.z, rot.w));
            euler.z += delta * 1.5; // Z rotation counter-clockwise més viva
            euler.x = 0; // Fixem l'isometria perfectament sense "tambaleig"
            euler.y = 0;
            const q = new THREE.Quaternion().setFromEuler(euler);
            outerRef.current.setNextKinematicRotation(q); // Important: forcem a motor físic a recalcular rotació

            if (spawnAnimation) {
                outerRef.current.setNextKinematicTranslation({ x: position[0] + xOffset.current, y: position[1], z: position[2] });
            }
        }

        // 2. Cub Petit (Gir Horari més ràpid exclusivament a l'eix Z)
        if (innerRef.current) {
            const rot = innerRef.current.rotation();
            const euler = new THREE.Euler().setFromQuaternion(new THREE.Quaternion(rot.x, rot.y, rot.z, rot.w));
            euler.z -= delta * 2.5; // Z rotation clockwise (més ràpid)
            euler.x = 0;
            euler.y = 0;
            const q = new THREE.Quaternion().setFromEuler(euler);
            innerRef.current.setNextKinematicRotation(q);

            if (spawnAnimation) {
                innerRef.current.setNextKinematicTranslation({ x: position[0] + xOffset.current, y: position[1], z: position[2] });
            }
        }
    });

    return (
        <group scale={scale}>
            <NeonObstacle ref={outerRef} position={position} spawnAnimation={spawnAnimation} type="kinematicPosition" geometry={outerGeom} color={color} coreOpacity={0.15} {...props} />
            <NeonObstacle ref={innerRef} position={position} spawnAnimation={spawnAnimation} type="kinematicPosition" geometry={innerGeom} color={innerColor} coreOpacity={0.4} {...props} />
        </group>
    );
}
